// Verbindungs-Pille im Kopf jeder Ansicht („● Gambio verbunden").
//
// BEFUND 2026-08-21: Der Text stand wörtlich im Markup von fünf Views und der Punkt war in sechs
// Stylesheets fest auf grün gesetzt. Die Pille konnte also nur „verbunden" behaupten — auch als der
// Shop jeden Aufruf mit 401 ablehnte. Zusätzlich schrieb niemand `marketplace_connections.status`
// auf `invalid_credentials`. Der Zustand war für den Anwender damit unsichtbar.
//
// Diese Datei ersetzt die Behauptung durch eine Messung: EIN Aufruf der Edge Fn
// `gambio-connection-status` je Sitzung (die Views teilen sich das Promise), Ergebnis in alle
// vorhandenen Pillen. Rot heißt: der Shop lehnt uns ab, mit dem Grund im `title` und einem
// Hinweis auf die Einstellungen.
//
// Bewusst KEIN Dauer-Polling: der Zustand ändert sich selten, und jede Ansicht rendert ihren Kopf
// neu, wenn der Nutzer navigiert. `refreshConnectionPills()` erzwingt eine neue Messung, z. B.
// nach dem Speichern neuer Zugangsdaten.

interface PillCtx {
  supabaseUrl: string;
  token: string;
  shopKey?: string;
}

interface StatusResult {
  ok: boolean;
  status?: string;
  code?: string;
  message?: string;
}

/**
 * Wie lange ein Messergebnis gilt.
 *
 * VORFALL 2026-08-21 (erster Kunde): Die Messung lief EINMAL pro Seitenaufruf und das Ergebnis
 * wurde modulweit gecacht (`if (last) return last`). Neu gemessen wurde nur beim Speichern von
 * Zugangsdaten. Ging die erste Messung daneben — bei diesem Kunden, weil die Shop-API gerade
 * ausfiel — blieb die Pille die GANZE Sitzung auf „unbekannt" bzw. rot, obwohl laengst alles lief.
 * Der Betreiber berichtete deshalb, die gruene Anzeige „habe es noch nie gegeben".
 *
 * Deshalb zwei Haltbarkeiten statt einer Ewigkeit: ein gutes Ergebnis ist eine Minute lang
 * verlaesslich, ein schlechtes wird schon nach fuenf Sekunden erneut geprueft. Damit heilt die
 * Pille von selbst, sobald der Nutzer navigiert oder die Ansicht neu rendert.
 */
const OK_TTL_MS = 60_000;
const FAIL_TTL_MS = 5_000;

let ctx: PillCtx | null = null;
let pending: Promise<StatusResult> | null = null;
let last: StatusResult | null = null;
let lastAt = 0;
/** Testbarer Zeitgeber — die Tests setzen ihn, damit sie nicht echt warten muessen. */
let nowFn: () => number = () => Date.now();

/** Pause vor dem einmaligen Nachfassen. In Tests auf 0 gesetzt, damit sie nicht echt warten. */
let retryDelayMs = 700;

/** Nur fuer Tests: Uhr, Cache und Retry-Pause zuruecksetzen. */
export function __resetPillCacheForTests(now?: () => number, retryMs?: number): void {
  pending = null;
  last = null;
  lastAt = 0;
  nowFn = now ?? (() => Date.now());
  retryDelayMs = retryMs ?? 700;
}

/** Gilt das gespeicherte Ergebnis noch? Fehlschlaege verfallen absichtlich viel schneller. */
function cacheIsFresh(): boolean {
  if (!last) return false;
  const ttl = last.ok ? OK_TTL_MS : FAIL_TTL_MS;
  return nowFn() - lastAt < ttl;
}

/** Einmal beim App-Start aufrufen (main.ts), damit die Pillen wissen, wohin sie fragen. */
export function initConnectionPills(next: PillCtx): void {
  ctx = next;
  pending = null;
  last = null;
  lastAt = 0;
  // Jetzt erst ist ein Messen sinnvoll: vorher gab es keinen Kontext (kein supabaseUrl/Token).
  observeForPills();
  void paintAll();
}

/**
 * URSACHE des Dauerzustands „Verbindung wird geprüft" (Betreiber-Meldung 2026-08-24):
 *
 * Die Views bauen ihre Kopfzeile als MODULWEITE Konstante (`const HEADER_HTML = ...` mit
 * `${connectionPillHtml()}`). Diese Konstante wird EINMAL beim Laden des Moduls ausgewertet —
 * also bevor die Kopfzeile im DOM steht und bevor `initConnectionPills()` den Kontext gesetzt hat.
 * Der frueher hier verwendete `queueMicrotask(paintAll)` lief damit gegen ein DOM ohne Pille,
 * fand nichts, und danach loeste nichts mehr ein Neuzeichnen aus. Die Pille blieb fuer immer im
 * Anfangszustand — genau das, was der Betreiber als „die gruene Anzeige gab es noch nie"
 * beschrieben hat. Der Zwischenspeicher war nur die zweite Schicht des Problems.
 *
 * Loesung: nicht auf den richtigen Zeitpunkt hoffen, sondern auf das DOM hoeren. Ein
 * MutationObserver zeichnet jede neu eingefuegte, noch nicht gezeichnete Pille — unabhaengig
 * davon, welche View sie wann einsetzt. Das kommt ohne Aenderung an den fuenf Views aus.
 */
let observer: MutationObserver | null = null;

function observeForPills(): void {
  if (observer || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
  // `main.ts` ruft init nach dem DOM-Aufbau, aber ein fehlender body waere ein harter Wurf —
  // lieber still nichts tun, die Pille wird dann beim naechsten Aufruf gezeichnet.
  if (!document.body) return;
  observer = new MutationObserver(() => {
    // Nur handeln, wenn es wirklich eine ungezeichnete Pille gibt: der Beobachter feuert bei
    // JEDER DOM-Aenderung, und ein Messen pro Tastendruck waere unsinnig.
    if (document.querySelector('[data-conn-pill]:not([data-conn-painted])')) void paintAll();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Markup der Pille. Startet im Zustand „wird geprüft" und aktualisiert sich selbst, sobald die
 * Messung vorliegt — der Aufrufer muss also nichts weiter tun als dieses HTML einzusetzen.
 */
export function connectionPillHtml(): string {
  // KEIN queueMicrotask hier: diese Funktion wird von den Views in einer modulweiten Konstante
  // aufgerufen und laeuft damit lange vor dem DOM-Einbau (siehe observeForPills). Das Zeichnen
  // uebernehmen `initConnectionPills` und der MutationObserver, die beide zum richtigen Zeitpunkt
  // greifen. Ein Marker statt einer ID, weil mehrere Ansichten gleichzeitig im DOM stehen koennen.
  return '<span class="hstatus hstatus--checking" data-conn-pill><span class="d"></span>Verbindung wird geprüft …</span>';
}

/** Neu messen (z. B. nach „Speichern & prüfen" in den Einstellungen) und alle Pillen aktualisieren. */
export async function refreshConnectionPills(): Promise<void> {
  pending = null;
  last = null;
  lastAt = 0;
  if (typeof document !== 'undefined') {
    for (const p of document.querySelectorAll('[data-conn-pill]')) p.removeAttribute('data-conn-painted');
  }
  await paintAll();
}

/** Ein einzelner Aufruf der Pruef-Function. Wirft nicht, sondern liefert immer ein Ergebnis. */
async function probeOnce(c: PillCtx): Promise<StatusResult> {
  try {
    const res = await fetch(`${c.supabaseUrl}/functions/v1/gambio-connection-status`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${c.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ shopKey: c.shopKey ?? 'default' }),
    });
    const out = (await res.json().catch(() => ({}))) as StatusResult & { error?: { message?: string } };
    // 5xx/HTML von unserer eigenen Funktion sagt NICHTS über den Shop. Das als
    // „Gambio nicht erreichbar" auszugeben, wäre eine falsche Beschuldigung.
    if (!res.ok) {
      return { ok: false, status: 'check_failed', message: out.error?.message ?? `HTTP ${res.status}` };
    }
    return out;
  } catch (e) {
    return { ok: false, status: 'check_failed', message: (e as Error).message };
  }
}

export async function checkOnce(): Promise<StatusResult> {
  if (cacheIsFresh()) return last!;
  if (!pending) {
    pending = (async (): Promise<StatusResult> => {
      if (!ctx) return { ok: true }; // ohne Kontext nichts behaupten → wie bisher „verbunden"
      const first = await probeOnce(ctx);
      // Scheitert unsere EIGENE Pruefung (Netz-Schluckauf, kalte Function, abgelaufenes Token im
      // Moment des Wechsels), einmal kurz nachfassen, bevor wir dem Nutzer „unbekannt" zeigen.
      // Ein Urteil ueber den Shop (`invalid_credentials`, `unreachable`) wird NICHT wiederholt —
      // das ist eine Antwort, kein Ausfall, und ein zweiter Aufruf wuerde nur Last erzeugen.
      if (first.status !== 'check_failed') return first;
      await new Promise((r) => setTimeout(r, retryDelayMs));
      return probeOnce(ctx);
    })();
  }
  const result = await pending;
  pending = null;
  last = result;
  lastAt = nowFn();
  return result;
}

async function paintAll(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (document.querySelectorAll('[data-conn-pill]').length === 0) return; // noch nichts zu zeichnen
  const result = await checkOnce();
  const pills = document.querySelectorAll<HTMLElement>('[data-conn-pill]');
  for (const pill of pills) applyState(pill, result);
}

/** Pure genug für einen Test: setzt Klasse, Text und Titel aus dem Messergebnis. */
export function applyState(pill: HTMLElement, result: StatusResult): void {
  const { cls, text, title } = pillState(result);
  pill.className = cls;
  pill.title = title;
  pill.innerHTML = `<span class="d"></span>${text}`;
  // Markierung, damit der Beobachter eine bereits gezeichnete Pille nicht als Anlass fuer eine
  // neue Messung nimmt. `className` wird oben ueberschrieben, deshalb ein eigenes Attribut.
  pill.setAttribute('data-conn-painted', '1');
}

/** Zustand → Klasse/Text/Titel. Ohne DOM, damit testbar. */
export function pillState(result: StatusResult): { cls: string; text: string; title: string } {
  if (result.ok) {
    return { cls: 'hstatus', text: 'Gambio verbunden', title: 'Der Shop antwortet auf API-Aufrufe.' };
  }
  if (result.status === 'invalid_credentials') {
    const reason = result.code === 'FORBIDDEN'
      ? 'Der Benutzer hat keine REST-Berechtigungen im Shop.'
      : 'Der Shop lehnt die hinterlegten Zugangsdaten ab.';
    return {
      cls: 'hstatus hstatus--error',
      text: 'Gambio nicht verbunden',
      title: `${reason} Bitte in den Einstellungen erneuern.`,
    };
  }
  if (result.status === 'check_failed') {
    // Unsere Prüfung selbst ist gescheitert (Netz, Edge Function). Kein Urteil über den Shop:
    // grau statt rot, damit niemand einen Shop-Ausfall vermutet, den es nicht gibt.
    return {
      cls: 'hstatus hstatus--checking',
      text: 'Verbindungsstatus unbekannt',
      title: result.message ? `Die Prüfung ist gescheitert: ${result.message}` : 'Die Prüfung ist gescheitert.',
    };
  }
  return {
    cls: 'hstatus hstatus--error',
    text: 'Gambio nicht erreichbar',
    title: result.message ? `Der Shop antwortet nicht: ${result.message}` : 'Der Shop antwortet nicht.',
  };
}
